import { stringIncludesInsensitiveAccents } from '../../utils/normalizeForSearch';

/**
 * @param {ApiDetailHeaderMenu | ApiDetailHeaderMenuItem | null | undefined} item
 * @returns {boolean}
 */
export function hasDetailHeaderMenuChildren(item) {
  return Array.isArray(item?.items) && item.items.length > 0;
}

/**
 * Navigation is only allowed on leaf entries with a non-empty baseUrl.
 * @param {ApiDetailHeaderMenu | ApiDetailHeaderMenuItem | null | undefined} item
 * @returns {boolean}
 */
export function isDetailHeaderMenuItemClickable(item) {
  if (!item || hasDetailHeaderMenuChildren(item)) {
    return false;
  }
  const { baseUrl } = item;
  return typeof baseUrl === 'string' && baseUrl.length > 0;
}

/**
 * Stable React key for a menu entry (depth disambiguates duplicate labels).
 * @param {ApiDetailHeaderMenu | ApiDetailHeaderMenuItem} item
 * @param {number} depth
 * @returns {string}
 */
export function getDetailHeaderMenuItemKey(item, depth) {
  const base = item?.baseUrl ?? 'group';
  return `${depth}-${item?.lib ?? ''}-${base}-${item?.iconColor ?? ''}`;
}

/**
 * Pastille color when the API provides a non-empty icon (swatch) and iconColor.
 * @param {ApiDetailHeaderMenu | ApiDetailHeaderMenuItem} item
 * @returns {string}
 */
export function getDetailHeaderMenuSwatchColor(item) {
  const icon = item?.icon;
  const iconColor = item?.iconColor;
  if (!icon || !iconColor) {
    return '';
  }
  return iconColor;
}

/**
 * Filter detail header menu entries by lib or optGroup (case- and accent-insensitive).
 * Matching parents keep all children; matching descendants keep their ancestors.
 * @param {Array<ApiDetailHeaderMenu | ApiDetailHeaderMenuItem> | null | undefined} items
 * @param {string} search
 * @returns {Array<ApiDetailHeaderMenu | ApiDetailHeaderMenuItem>}
 */
export function filterDetailHeaderMenuBySearch(items, search) {
  if (!items?.length) return [];

  const searchTrimmed = (search || '').trim();
  if (!searchTrimmed) return items;

  return items.reduce((filtered, item) => {
    const libMatch = stringIncludesInsensitiveAccents(item.lib, searchTrimmed);
    const optGroupMatch = stringIncludesInsensitiveAccents(
      'optGroup' in item ? item.optGroup : null,
      searchTrimmed,
    );
    const itemMatches = libMatch || optGroupMatch;
    const filteredChildren = item.items
      ? filterDetailHeaderMenuBySearch(item.items, search)
      : [];
    const hasMatchingChildren = filteredChildren.length > 0;

    if (itemMatches || hasMatchingChildren) {
      if (itemMatches && item.items) {
        filtered.push({
          ...item,
          items: item.items,
        });
      } else {
        filtered.push({
          ...item,
          ...(item.items && { items: filteredChildren }),
        });
      }
    }

    return filtered;
  }, []);
}

/**
 * Determine which field type is non-null in ApiDetailHeaderField
 * @param {ApiDetailHeaderField} field - The field to check
 * @returns {{ type: string | null, data: any }} The field type and its data
 */
export const getDetailHeaderFieldType = (field) => {
  if (!field) return { type: null, data: null };

  if (field.text) return { type: 'text', data: field.text };
  if (field.table) return { type: 'table', data: field.table };
  if (field.boolean) return { type: 'boolean', data: field.boolean };
  if (field.image) return { type: 'image', data: field.image };

  return { type: null, data: null };
};
